import {
  CalendarIcon,
  EditIcon,
  EyeIcon,
  EyeOffIcon,
  HashIcon,
  IconComponent,
  ListIcon,
  RupeeIcon,
  TypeIcon,
  UploadCloudIcon,
} from '@razorpay/blade/components';
import { routePaths } from 'components/Routes/data';
import {
  EmployeeFieldAccess,
  EmployeeFieldValueType,
  FieldPreviewState,
  FieldTypeMeta,
} from './types';

export const employeeDataTabs = {
  general: 'general',
  fields: 'fields',
} as const;

export const getEmployeeDataTabItems = () => [
  {
    children: 'General',
    value: employeeDataTabs.general,
    path: routePaths.settings.employeeData.general,
  },
  {
    children: 'Employee Fields',
    value: employeeDataTabs.fields,
    path: routePaths.settings.employeeData.fields,
  },
];

export const FIELD_TYPE_OPTIONS: Array<{
  label: string;
  value: EmployeeFieldValueType;
  helperText: string;
}> = [
  { label: 'Text', value: 'STRING', helperText: 'Words, names, IDs, or short notes' },
  { label: 'Number', value: 'INTEGER', helperText: 'Whole numbers without decimals' },
  { label: 'Decimal', value: 'DECIMAL', helperText: 'Numbers with decimal precision' },
  { label: 'Currency (₹)', value: 'CURRENCY', helperText: 'Money values in rupees' },
  { label: 'Date', value: 'DATE', helperText: 'A calendar date' },
  { label: 'Yes/No', value: 'BOOLEAN', helperText: 'A boolean answer' },
  { label: 'Dropdown list', value: 'ENUM', helperText: 'A fixed list of options' },
  { label: 'File Upload', value: 'FILE', helperText: 'An uploaded document or file' },
];

export const FIELD_TYPE_META: Record<EmployeeFieldValueType, FieldTypeMeta> = {
  STRING: {
    Icon: TypeIcon,
    helperText: 'Words, names, IDs, or short notes',
    selectedHelperText: 'Employees can enter words, names, IDs, or short notes.',
  },
  INTEGER: {
    Icon: HashIcon,
    helperText: 'Whole numbers without decimals',
    selectedHelperText: 'Employees can enter whole numbers without decimals.',
  },
  DECIMAL: {
    Icon: HashIcon,
    helperText: 'Numbers with decimal precision',
    selectedHelperText: 'Employees can enter numbers with decimal precision.',
  },
  CURRENCY: {
    Icon: RupeeIcon,
    helperText: 'Money values in rupees',
    selectedHelperText: 'Employees can enter money values in rupees.',
  },
  DATE: {
    Icon: CalendarIcon,
    helperText: 'Calendar dates, e.g. "15 Jun 2026"',
    selectedHelperText: 'Employees can enter calendar dates.',
  },
  BOOLEAN: {
    Icon: EyeIcon,
    helperText: 'Yes or no answers',
    selectedHelperText: 'Employees can choose a yes or no answer.',
  },
  ENUM: {
    Icon: ListIcon,
    helperText: 'Choose from a list of options',
    selectedHelperText: 'Choose from a list of options',
  },
  FILE: {
    Icon: UploadCloudIcon,
    helperText: 'Documents or supporting files',
    selectedHelperText: 'Employees can upload documents or supporting files.',
  },
};

export const ACCESS_LABELS: Record<EmployeeFieldAccess, string> = {
  EDIT: 'Edit',
  VIEW_ONLY: 'View only',
  HIDDEN: 'Hidden',
};

export const ACCESS_META: Record<EmployeeFieldAccess, { Icon: IconComponent }> = {
  EDIT: { Icon: EditIcon },
  VIEW_ONLY: { Icon: EyeIcon },
  HIDDEN: { Icon: EyeOffIcon },
};

export const ADD_FIELD_WIZARD_STEPS = ['Field details', 'Permissions', 'Review'];

export const EMPTY_FIELD_PREVIEW: FieldPreviewState = {
  textValue: '',
  dateValue: null,
  booleanValue: '',
};

/** Number of placeholder panels rendered while the General tab settings load. */
export const GENERAL_SETTINGS_SKELETON_PANEL_COUNT = 3;

/** Number of placeholder rows rendered while the employee fields table loads. */
export const EMPLOYEE_FIELDS_SKELETON_ROW_COUNT = 6;
